import React, { useState, useEffect } from 'react';
import { playertoolsAPI, playersAPI, projectsAPI, teamsAPI } from '../../services/api';

const PlayerAssignments = () => {
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAssignment, setNewAssignment] = useState({
    projectId: '',
    teamId: '',
    jerseyNumber: '',
    published: true
  });  const [addingAssignment, setAddingAssignment] = useState(false);
  const [addError, setAddError] = useState('');
  
  // Edit Assignment States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editAssignment, setEditAssignment] = useState({
    projectId: '',
    teamId: '',
    jerseyNumber: '',
    published: true
  });
  const [editingAssignmentLoading, setEditingAssignmentLoading] = useState(false);
  const [editError, setEditError] = useState('');
  useEffect(() => {
    fetchPlayers();
    fetchProjects();
  }, []);

  useEffect(() => {
    // Filter players based on search term
    if (searchTerm.trim() === '') {
      setFilteredPlayers(players);
    } else {
      const filtered = players.filter(player => 
        `${player.prename} ${player.surname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.prename.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPlayers(filtered);
    }
  }, [searchTerm, players]);

  // Reset teams when modal is closed
  useEffect(() => {
    if (!showAddModal) {
      setTeams([]);
      setNewAssignment({
        projectId: '',
        teamId: '',
        jerseyNumber: '',
        published: true
      });
    }
  }, [showAddModal]);

  const fetchPlayers = async () => {
    setLoadingPlayers(true);
    try {
      // Request all players by setting a high size limit
      const response = await playersAPI.getAll({ size: 1000 });
      // Handle different response structures
      const playersData = response.data?.players || response.data || [];
      console.log('Raw API response:', response.data);
      console.log('Processed players data:', playersData);
      
      if (Array.isArray(playersData)) {
        setPlayers(playersData);
        setFilteredPlayers(playersData);
      } else {
        console.error('Players data is not an array:', playersData);
        setPlayers([]);
        setFilteredPlayers([]);
        setError('Invalid players data format');
      }
    } catch (error) {
      console.error('Error fetching players:', error);
      setError('Failed to load players');
      setPlayers([]);
      setFilteredPlayers([]);
    } finally {
      setLoadingPlayers(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchTeamsForProject = async (projectId) => {
    if (!projectId) {
      setTeams([]);
      return;
    }
    
    try {
      const response = await teamsAPI.getByProject(projectId);
      setTeams(response.data || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setTeams([]);
    }
  };

  const fetchPlayerAssignments = async (playerId) => {
    if (!playerId) {
      setAssignments([]);
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await playertoolsAPI.getByPlayer(playerId);
      setAssignments(response.data.playertools || []);
    } catch (error) {
      console.error('Error fetching player assignments:', error);
      setError('Failed to load player assignments');
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerChange = (e) => {
    const playerId = e.target.value;
    setSelectedPlayer(playerId);
    fetchPlayerAssignments(playerId);
  };
  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      await playertoolsAPI.delete(assignmentId);
      // Refresh assignments
      fetchPlayerAssignments(selectedPlayer);
    } catch (error) {
      console.error('Error deleting assignment:', error);
      setError('Failed to delete assignment');
    }
  };

  const handleAddAssignment = () => {
    if (!selectedPlayer) {
      setError('Please select a player first');
      return;
    }
    setNewAssignment({
      projectId: '',
      teamId: '',
      jerseyNumber: '',
      published: true
    });
    setAddError('');
    setShowAddModal(true);
  };

  const handleProjectChange = (projectId) => {
    setNewAssignment(prev => ({
      ...prev,
      projectId,
      teamId: '' // Reset team when project changes
    }));
    fetchTeamsForProject(projectId);
  };
  const handleSubmitAssignment = async (e) => {
    e.preventDefault();
    
    if (!newAssignment.projectId || !newAssignment.teamId) {
      setAddError('Please select both project and team');
      return;
    }

    setAddingAssignment(true);
    setAddError('');

    try {
      const assignmentData = {
        plid: selectedPlayer,
        pid: newAssignment.projectId,
        tid: newAssignment.teamId,
        num: newAssignment.jerseyNumber ? parseInt(newAssignment.jerseyNumber) : 0,
        published: newAssignment.published
      };

      await playertoolsAPI.create(assignmentData);
      
      // Refresh assignments and close modal
      fetchPlayerAssignments(selectedPlayer);
      setShowAddModal(false);
      setNewAssignment({
        projectId: '',
        teamId: '',
        jerseyNumber: '',
        published: true
      });
    } catch (error) {
      console.error('Error creating assignment:', error);
      setAddError(error.response?.data?.message || 'Failed to create assignment');
    } finally {
      setAddingAssignment(false);
    }
  };

  const handleEditAssignment = async (assignment) => {
    setEditingAssignment(assignment);
    setEditAssignment({
      projectId: assignment.pid?._id || '',
      teamId: assignment.tid?._id || '',
      jerseyNumber: assignment.num !== undefined && assignment.num !== null ? assignment.num.toString() : '',
      published: assignment.published
    });
    
    // Load teams for the project
    if (assignment.pid?._id) {
      await fetchTeamsForProject(assignment.pid._id);
    }
    
    setEditError('');
    setShowEditModal(true);
  };

  const handleEditProjectChange = (projectId) => {
    setEditAssignment(prev => ({
      ...prev,
      projectId,
      teamId: '' // Reset team when project changes
    }));
    fetchTeamsForProject(projectId);
  };

  const handleSubmitEditAssignment = async (e) => {
    e.preventDefault();
    
    if (!editAssignment.projectId || !editAssignment.teamId) {
      setEditError('Please select both project and team');
      return;
    }

    setEditingAssignmentLoading(true);
    setEditError('');

    try {
      const assignmentData = {
        plid: selectedPlayer,
        pid: editAssignment.projectId,
        tid: editAssignment.teamId,
        num: editAssignment.jerseyNumber ? parseInt(editAssignment.jerseyNumber) : 0,
        published: editAssignment.published
      };

      await playertoolsAPI.update(editingAssignment._id, assignmentData);
      
      // Refresh assignments and close modal
      fetchPlayerAssignments(selectedPlayer);
      setShowEditModal(false);
      setEditingAssignment(null);
      setEditAssignment({
        projectId: '',
        teamId: '',
        jerseyNumber: '',
        published: true
      });
    } catch (error) {
      console.error('Error updating assignment:', error);
      setEditError(error.response?.data?.message || 'Failed to update assignment');
    } finally {
      setEditingAssignmentLoading(false);
    }
  };

  const selectedPlayerData = players && Array.isArray(players) 
    ? players.find(p => p._id === selectedPlayer)
    : null;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">
            Player Assignments
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View all project and team assignments for players
          </p>
        </div>        {/* Player Selection */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search and Select Player:
              </label>
              
              {/* Search Input */}
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Search players by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              {/* Player Dropdown */}
              <select
                value={selectedPlayer}
                onChange={handlePlayerChange}
                disabled={loadingPlayers}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
              >
                <option value="">
                  {loadingPlayers ? 'Loading players...' : 'Choose a player...'}
                </option>
                {Array.isArray(filteredPlayers) && filteredPlayers.map(player => (
                  <option key={player._id} value={player._id}>
                    {player.surname}, {player.prename}
                  </option>
                ))}
              </select>
              
              {filteredPlayers.length === 0 && searchTerm && !loadingPlayers && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  No players found matching "{searchTerm}"
                </p>
              )}
            </div>
            
            {/* Add Assignment Button */}
            {selectedPlayer && (
              <div className="flex items-end">
                <button
                  onClick={handleAddAssignment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  Add Assignment
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Selected Player Info */}
        {selectedPlayerData && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
            <h2 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
              {selectedPlayerData.prename} {selectedPlayerData.surname}
            </h2>
            <p className="text-blue-600 dark:text-blue-300">
              Position: {selectedPlayerData.posid?.name || 'Not specified'}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-8">
            <div className="loading loading-spinner loading-lg"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading assignments...</p>
          </div>
        )}

        {/* Assignments List */}
        {!loading && selectedPlayer && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                Assignments ({assignments.length})
              </h3>
            </div>

            {assignments.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                No assignments found for this player.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Project
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Team
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Jersey #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {assignments.map((assignment) => (
                      <tr key={assignment._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {assignment.pid?.name || 'Unknown Project'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {assignment.tid?.name || 'Unknown Team'}
                        </td>                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            #{(assignment.num !== undefined && assignment.num !== null) ? assignment.num.toString() : 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            assignment.published 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          }`}>
                            {assignment.published ? 'Published' : 'Draft'}
                          </span>
                        </td>                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditAssignment(assignment)}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteAssignment(assignment._id)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>            )}
          </div>
        )}

        {/* Add Assignment Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Add New Assignment
                  </h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmitAssignment}>
                  {/* Selected Player Info */}
                  {selectedPlayerData && (
                    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        Player: {selectedPlayerData.prename} {selectedPlayerData.surname}
                      </p>
                    </div>
                  )}

                  {/* Project Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Project *
                    </label>
                    <select
                      value={newAssignment.projectId}
                      onChange={(e) => handleProjectChange(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select Project...</option>
                      {projects.map(project => (
                        <option key={project._id} value={project._id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Team Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Team *
                    </label>
                    <select
                      value={newAssignment.teamId}
                      onChange={(e) => setNewAssignment(prev => ({ ...prev, teamId: e.target.value }))}
                      required
                      disabled={!newAssignment.projectId}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                    >
                      <option value="">
                        {!newAssignment.projectId ? 'Select project first...' : 'Select Team...'}
                      </option>
                      {teams.map(team => (
                        <option key={team._id} value={team._id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Jersey Number */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Jersey Number
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={newAssignment.jerseyNumber}
                      onChange={(e) => setNewAssignment(prev => ({ ...prev, jerseyNumber: e.target.value }))}
                      placeholder="Optional"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Published Status */}
                  <div className="mb-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newAssignment.published}
                        onChange={(e) => setNewAssignment(prev => ({ ...prev, published: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Published (visible in widgets)
                      </span>
                    </label>
                  </div>

                  {/* Error Message */}
                  {addError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                      {addError}
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={addingAssignment}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addingAssignment ? 'Adding...' : 'Add Assignment'}
                    </button>                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Assignment Modal */}
        {showEditModal && editingAssignment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Edit Assignment
                  </h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmitEditAssignment}>
                  {/* Project Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Project *
                    </label>
                    <select
                      value={editAssignment.projectId}
                      onChange={(e) => handleEditProjectChange(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select Project...</option>
                      {projects.map(project => (
                        <option key={project._id} value={project._id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Team Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Team *
                    </label>
                    <select
                      value={editAssignment.teamId}
                      onChange={(e) => setEditAssignment(prev => ({ ...prev, teamId: e.target.value }))}
                      required
                      disabled={!editAssignment.projectId}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                    >
                      <option value="">
                        {!editAssignment.projectId ? 'Select project first...' : 'Select Team...'}
                      </option>
                      {teams.map(team => (
                        <option key={team._id} value={team._id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Jersey Number */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Jersey Number
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={editAssignment.jerseyNumber}
                      onChange={(e) => setEditAssignment(prev => ({ ...prev, jerseyNumber: e.target.value }))}
                      placeholder="Optional"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Published Status */}
                  <div className="mb-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editAssignment.published}
                        onChange={(e) => setEditAssignment(prev => ({ ...prev, published: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Published (visible in widgets)
                      </span>
                    </label>
                  </div>

                  {/* Error Message */}
                  {editError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                      {editError}
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={editingAssignmentLoading}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editingAssignmentLoading ? 'Updating...' : 'Update Assignment'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerAssignments;
